// Users in the caller's org. Backed by the Cortex API's `/v1/orgs/me/*`
// endpoints — the browser carries the user's JWT, the API authorizes the
// caller, and the API uses its own Keycloak service-account credentials to
// touch the admin REST. Open to any org member (flat admin model).
import { useClient } from '@cortex/react-query';
import type { OrgMember } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { Modal } from '../components/common/Modal';
import { PageHeader } from '../components/common/PageHeader';
import { Table, type IColumn } from '../components/common/Table';
import { useToast } from '../components/common/Toast';
import { useOrgContext } from '../hooks/useOrgContext';
import { readApiValidationErrors } from '../lib/api-errors';
import { inviteMyOrgMember, listMyOrgMembers, setMyOrgMemberEnabled } from '../lib/orgs';

export const Route = createFileRoute('/users')({ component: UsersPage });

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

function UsersPage() {
  const client = useClient();
  const { orgId } = useOrgContext();
  const qc = useQueryClient();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);

  const list = useQuery({
    enabled: Boolean(orgId),
    queryKey: ['org-members', orgId],
    queryFn: () => listMyOrgMembers(client),
  });

  const invalidate = async () => {
    try {
      await qc.invalidateQueries({ queryKey: ['org-members', orgId] });
    } catch (err) {
      console.error(err);
    }
  };

  const toggleEnabled = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; email: string; enabled: boolean }) =>
      setMyOrgMemberEnabled(client, userId, enabled),
    onSuccess: async (_data, vars) => {
      await invalidate();
      toast.show({
        kind: 'success',
        text: vars.enabled ? `Enabled ${vars.email}` : `Disabled ${vars.email}`,
      });
    },
    onError: (err) => {
      toast.show({ kind: 'error', text: `Failed: ${(err as Error).message}` });
    },
  });

  const cols: IColumn<OrgMember>[] = [
    {
      key: 'email',
      header: 'Email',
      cell: (u) => <span style={{ color: 'var(--text-primary)' }}>{u.email}</span>,
    },
    { key: 'name', header: 'Name', cell: (u) => u.name ?? <span className="t-muted">—</span> },
    {
      key: 'enabled',
      header: 'Status',
      cell: (u) =>
        u.enabled ? (
          <span className="badge badge--success">Active</span>
        ) : (
          <span className="badge badge--neutral">Disabled</span>
        ),
      width: 120,
    },
    {
      key: 'actions',
      header: '',
      cell: (u) => (
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          onClick={() => {
            const next = !u.enabled;
            const verb = next ? 'Enable' : 'Disable';
            if (confirm(`${verb} ${u.email}?`))
              toggleEnabled.mutate({ userId: u.id, email: u.email, enabled: next });
          }}
        >
          {u.enabled ? 'Disable' : 'Enable'}
        </button>
      ),
      width: 120,
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        actions={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setInviting(true)}
          >
            Invite user
          </button>
        }
      />
      {!orgId ? (
        <div className="alert alert--warning">No organization context found in your token.</div>
      ) : (
        <Table<OrgMember>
          rows={list.data}
          columns={cols}
          rowKey={(u) => u.id}
          isLoading={list.isLoading}
          error={list.error as Error | null}
          emptyTitle="No users yet"
          emptyDescription="Invite teammates by email to give them access."
        />
      )}
      <InviteModal
        open={inviting}
        onClose={() => setInviting(false)}
        onInvited={async () => {
          await invalidate();
          setInviting(false);
        }}
      />
      {/* InviteModal's own success path shows the toast; we just close + refresh. */}
    </>
  );
}

function InviteModal({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: (email: string) => void;
}) {
  const client = useClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const invite = useMutation({
    mutationFn: () => inviteMyOrgMember(client, { email, name: name.trim() || undefined }),
    onSuccess: () => {
      const invited = email;
      setEmail('');
      setName('');
      toast.show({ kind: 'success', text: `Invite sent to ${invited}` });
      onInvited(invited);
    },
    onError: async (err) => {
      const lines = await readApiValidationErrors(err);
      toast.show({
        kind: 'error',
        text: lines.length > 0 ? lines.join(' · ') : (err as Error).message,
      });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite user"
      eyebrow="ORG · USERS"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              if (email.trim()) invite.mutate();
            }}
            disabled={invite.isPending || !email.trim()}
          >
            {invite.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </>
      }
    >
      <form style={stack3} onSubmit={(e) => e.preventDefault()}>
        <label className="field">
          <span className="field-label">Email</span>
          <input
            type="email"
            required={true}
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alice@formant.io"
          />
        </label>
        <label className="field">
          <span className="field-label">Display name (optional)</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alice Example"
          />
        </label>
        {invite.error ? (
          <div className="alert alert--danger">{(invite.error as Error).message}</div>
        ) : null}
        <p className="t-xs t-muted">
          The invitee receives an email with a one-click link to set their password. After they
          finish, they&apos;re redirected back here, signed in.
        </p>
      </form>
    </Modal>
  );
}
